import { DictionaryRecordDto } from '@lmt/shared-types';
import {
  NS_DataBlockFields,
  useScopedTranslation,
} from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';

import { useEventCallback } from '@mui/material';
import React, { FC, useMemo } from 'react';
import { TableDotMenu } from '../../../common/table/TableDotMenu';
import {
  SortOrder,
  RowClickEvent,
  RowPreparedEvent,
} from '@lmt/unicorn-shared/app/common/devexpress/Grid/dx-grid';
import { DataGrid } from '../../../common/dataGrid/DataGrid';

type Props = {
  noResults: boolean;
  data: DictionaryRecordDto[];
  onDelete: (recordId: number) => void;
  onEdit: (recordId: number) => void;
  prefix: string;
};

function isEditable(record: DictionaryRecordDto, prefix: string): boolean {
  // we can't edit default values
  return !record.value.startsWith(prefix);
}

const TableKeys: { [dictionary: string]: string } = {
  value: 'value',
  option: 'option',
};

export const DictionaryRecordsTable: FC<Props> = React.memo(
  function DictionaryRecordsTable(props) {
    const i18n = useScopedTranslation('dictionaries.list', NS_DataBlockFields);

    const onRowClick = useEventCallback((e: RowClickEvent) => {
      const editable = isEditable(e.data, props.prefix);
      if (editable) {
        props.onEdit(e.data.id);
      }
    });

    const onRowPrepared = useEventCallback((e: RowPreparedEvent) => {
      if (e.rowType === 'data') {
        const editable = isEditable(e.data, props.prefix);
        e.rowElement.setAttribute('data-enabled', String(editable));
      }
    });

    const columns = useMemo(
      () => [
        {
          key: TableKeys.value,
          dataField: TableKeys.value,
          caption: i18n.t(`columns.${TableKeys.value}` as any) as string,
          allowSorting: true,
          defaultSortOrder: 'asc' as SortOrder,
        },
        {
          key: TableKeys.option,
          caption: '',
          allowSorting: false,
          width: '60px',
          cellRender: (cell: any) => {
            const row = cell.data;
            return (
              <div
                onClick={(event: any) => event.stopPropagation()}
                style={{ margin: -6 }}
              >
                <TableDotMenu
                  options={[
                    {
                      title: i18n.t('options.edit_button'),
                      onClick: () => props.onEdit(row.id),
                    },
                    {
                      title: i18n.t('options.delete_button'),
                      onClick: () => props.onDelete(row.id),
                      isDeleteItem: true,
                      isUnderLine: true,
                    },
                  ]}
                />
              </div>
            );
          },
        },
      ],
      [],
    );

    return (
      <DataGrid
        dataSource={props.data}
        onRowClick={onRowClick}
        onRowPrepared={onRowPrepared}
        enableScrollTop={true}
        columns={columns}
      />
    );
  },
);
